import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Statistic,
  Table,
  Select,
  Button,
  Spin,
  Typography,
  Tag,
  Space,
  Input,
  Tabs,
  Card,
} from "antd";
import {
  DatabaseOutlined,
  WarningOutlined,
  StopOutlined,
  DollarCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";

const { Text } = Typography;
const { TabPane } = Tabs;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

const StockReports = () => {
  const [loading, setLoading] = useState(true);
  const [stockLevel, setStockLevel] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [outOfStock, setOutOfStock] = useState([]);
  const [stockValueByCategory, setStockValueByCategory] = useState([]);
  const [searchText, setSearchText] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [levelRes, lowRes, outRes, valueRes] = await Promise.all([
        axios.get(`${BASEURL}/reports/stock-level`, config),
        axios.get(`${BASEURL}/reports/low-stock`, config),
        axios.get(`${BASEURL}/reports/out-of-stock`, config),
        axios.get(`${BASEURL}/reports/stock-value-by-category`, config),
      ]);

      setStockLevel(levelRes.data);
      setLowStock(lowRes.data);
      setOutOfStock(outRes.data);
      setStockValueByCategory(valueRes.data);
    } catch (error) {
      console.error("Error fetching stock reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, fileName) => {
    const exportData = data.map((item, index) => ({
      "S.No": index + 1,
      "Product Title": item.title,
      Category: item.Category?.title || "N/A",
      Price: `₹${item.CurrentPrice}`,
      "Current Stock": item.quantity,
      "Ref Quantity": item.refQuantity,
      Unit: item.unit || "",
      Status: item.quantity <= 0 ? "Out of Stock" : item.quantity <= 10 ? "Low Stock" : "In Stock",

    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };


  const columns = [
    {
      title: "S.No",
      key: "index",
      width: 50,
      render: (_, __, i) => <span style={{ color: "#9ca3af" }}>{i + 1}</span>,
    },
    {
      title: "PRODUCT NAME",
      dataIndex: "title",
      key: "title",
      render: (t) => <span style={{ fontWeight: 600 }}>{t}</span>,
    },
    {
      title: "CATEGORY",
      dataIndex: "Category",
      key: "category",
      render: (c) => c?.title || "N/A",
    },
    {
      title: "PRICE",
      dataIndex: "CurrentPrice",
      key: "price",
      render: (p) => `₹${p}`,
    },
    {
      title: "UNIT",
      dataIndex: "unit",
      key: "unit",
      render: (u) => <Tag color="blue">{u || "Default"}</Tag>,
    },
    {
      title: "CURRENT STOCK",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      render: (q) => (
        <span style={{ fontWeight: 700, color: q <= 0 ? "#f5222d" : q <= 10 ? "#faad14" : "#52c41a" }}>
          {q}
        </span>
      ),
    },
    


    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_, record) => {
        let color = "green";
        let text = "In Stock";
        if (record.quantity <= 0) {
          color = "red";
          text = "Out of Stock";
        } else if (record.quantity <= 10) {
          color = "orange";
          text = "Low Stock";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  const totalStockValue = stockValueByCategory.reduce((acc, curr) => acc + curr.totalValue, 0);

  const filteredData = (data) =>
    data.filter((item) => item.title?.toLowerCase().includes(searchText.toLowerCase()));

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Loading Stock Reports..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Inventory Management
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: "Outfit,sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "#212121",
            }}
          >
            Stock Reports Dashboard
          </h2>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: 4 }}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={() => handleExport(stockLevel, "Full_Inventory_Report")}
            style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 4, fontWeight: 600 }}
          >
            Export All Stock
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Total SKUs</span>}
              value={stockLevel.length}
              prefix={<DatabaseOutlined style={{ color: "#1890ff", marginRight: 8 }} />}
              valueStyle={{ color: "#212121", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Low Stock Items</span>}
              value={lowStock.length}
              prefix={<WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />}
              valueStyle={{ color: "#faad14", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Out of Stock</span>}
              value={outOfStock.length}
              prefix={<StopOutlined style={{ color: "#f5222d", marginRight: 8 }} />}
              valueStyle={{ color: "#f5222d", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Total Stock Value</span>}
              value={totalStockValue}
              precision={2}
              prefix={<DollarCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />}
              valueStyle={{ color: "#52c41a", fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card 
            title={<span style={{ fontWeight: 700 }}>Stock Value by Category</span>} 
            bordered={false} 
            style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", height: '100%' }}
          >
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stockValueByCategory}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <Tooltip 
                    cursor={{ fill: '#f9fafb' }}
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="totalValue" fill="#1890ff" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title={<span style={{ fontWeight: 700 }}>Item Distribution</span>} 
            bordered={false} 
            style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", height: '100%' }}
          >
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stockValueByCategory}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="itemCount"
                    nameKey="_id"
                  >
                    {stockValueByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>


        {/* Inventory Tabs */}
        <Col xs={24}>
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e8ecf0",
              overflow: "hidden",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 16 }}>Inventory Details</span>
              <Input
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                placeholder="Search product..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250, borderRadius: 8 }}
              />
            </div>
            <div style={{ padding: "0 20px 20px" }}>
              <Tabs defaultActiveKey="1" className="custom-tabs">
                <TabPane tab={`All Stock (${stockLevel.length})`} key="1">
                  <Table
                    dataSource={filteredData(stockLevel)}
                    columns={columns}
                    rowKey="_id"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
                    size="middle"
                  />
                </TabPane>
                <TabPane tab={`Low Stock (${lowStock.length})`} key="2">
                  <Table
                    dataSource={filteredData(lowStock)}
                    columns={columns}
                    rowKey="_id"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
                    size="middle"
                  />
                </TabPane>
                <TabPane tab={`Out of Stock (${outOfStock.length})`} key="3">
                  <Table
                    dataSource={filteredData(outOfStock)}
                    columns={columns}
                    rowKey="_id"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
                    size="middle"
                  />
                </TabPane>
              </Tabs>
            </div>
          </div>
        </Col>
      </Row>
      <style>{`
        .custom-tabs .ant-tabs-nav::before {
          border-bottom: 1px solid #f3f4f6;
        }
        .custom-tabs .ant-tabs-tab {
          padding: 12px 16px;
          margin: 0;
          font-weight: 600;
        }
        .custom-tabs .ant-tabs-tab-active {
          color: #1890ff;
        }
        .report-card:hover {
          transform: translateY(-2px);
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important;
        }
      `}</style>
    </div>
  );
};

export default StockReports;

